<template>
  <div>
    <ValidationObserver ref="validator" slim v-slot="{ handleSubmit }">
      <h2 class="lib__h2">{{ $t('workFileTracking.appointment.title') }}</h2>
      <div class="row">
        <div class="col-xs-12 col-sm-6 col-md-12">
          <Feedback icon level="info" outline>
            <p v-html="$t('workFileTracking.appointment.text')"></p>
          </Feedback>
        </div>
      </div>
      <!-- API errors -->
      <ApiErrors />
      <div class="row">
        <div class="col-xs-12 col-sm-6 col-md-12">
          <!-- appointment -->
          <Appointment v-if="progressIndication.appointing" @input="onAppointmentInfoChange"
            @availabilitiesError="availabilitiesError" :createAppointmentStatus="createAppointmentStatus"
            :searchAvailabilitiesParameters="searchAvailabilitiesParameters" :searchEndDate="searchEndDate">
          </Appointment>
          <!-- send confirmation -->
          <SendConfirmation v-else-if="progressIndication.sendConfirmation" @close="onSendConfirmationClose"
            :createAppointmentStatus="createAppointmentStatus" :title="$t('appointment.confirmation.title')"
            :workFileReference="workFile.reference" :email="contactEmail" :appointmentInfo="appointment"
            :loading="loading">
          </SendConfirmation>
          <div class="lib__dialogs__footer">
            <nav class="lib__btns-nav-list">
              <a @click.prevent="handleSubmit(saveAppointment)" class="lib__btn lib__btn--primary lib__btn--rounded"
                :class="{ 'lib__btn--loading': loading, 'lib__btn--disabled': loading }" href="#"
                v-if="progressIndication.appointing">
                <span>{{ $t('workFileTracking.appointment.buttons.confirm') }}</span>
                <i class="fas fa-check"></i>
              </a>
            </nav>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import { Emit, Vue } from 'vue-property-decorator'
import { Mutation, State } from 'vuex-class'
import {
  AppointmentModificationProgress,
  GetWorkFileResponse,
  WorkFile,
  WorkFileIdentification
} from '@/models/work-file-tracking'
import ApiErrors from '@/components/display/ApiErrors.vue'
import Appointment from '@/components/inputs/Appointment/Appointment.vue'
import SendConfirmation from '@/components/inputs/Appointment/SendConfirmation.vue'
import { AxiosResponse } from 'axios'
import {
  AppointmentCancellationRequest,
  AppointmentInfo,
  CallbackRequestCreationRequest,
  CreateAppointmentResponse,
  CreateAppointmentStatus,
  GetAppointmentAvailabilitiesRequest,
  UpdateAppointmentRequest
} from '@/models'
import { appointmentService } from '@/services/appointment-service'
import moment from 'moment'

@Component({ components: { ApiErrors, Appointment, SendConfirmation } })
export default class AppointmentModification extends Vue {
  //#region [Props]
  //#endregion

  //#region [Data]
  @State('workFile', { namespace: 'workFileTracking/workFile' })
  public workFile!: WorkFile
  @State('requestId', { namespace: 'workFileTracking' })
  public requestId!: string
  @State('identification', { namespace: 'workFileTracking/identification' })
  public identificationState!: WorkFileIdentification | null

  private progressIndication: AppointmentModificationProgress = {
    appointing: false,
    sendConfirmation: false
  }

  private appointment: AppointmentInfo | null = null
  private loading: boolean = false

  private createAppointmentStatus: CreateAppointmentStatus = CreateAppointmentStatus.NoError

  //#endregion

  //#region [Mutations]
  @Mutation('STORE', { namespace: 'workFileTracking/workFile' })
  private storeFile!: (index: WorkFile | null) => void

  //#endregion

  //#region [Computed]
  public get searchAvailabilitiesParameters(): GetAppointmentAvailabilitiesRequest {
    return {
      workFileReference: this.workFile!.reference!,
      topic: appointmentService.appointmentWorkRequestTopic(this.workFile),
      service: appointmentService.appointmentWorkRequestService(this.workFile),
      workRequestType: appointmentService.appointmentWorkRequestType(this.workFile),
      powerLevel: appointmentService.appointmentPowerLevel(this.workFile),
      periodStartDate: this.currentSearchStartDate,
      periodEndDate: this.currentSearchEndDate,
      seeOtherDatesClicked: false
    }
  }

  public get appointmentCancellationParameters(): AppointmentCancellationRequest {
    return {
      workFileReference: this.workFile!.reference!,
      topic: appointmentService.appointmentWorkRequestTopic(this.workFile),
      service: appointmentService.appointmentWorkRequestService(this.workFile),
      workRequestType: appointmentService.appointmentWorkRequestType(this.workFile),
      powerLevel: appointmentService.appointmentPowerLevel(this.workFile)
    }
  }

  public get currentSearchStartDate(): Date {
    return appointmentService.workFileTrackingAppointmentSearchSuitableStartDate(new Date(), this.workFile)
  }

  public get currentSearchEndDate(): Date {
    return moment(this.currentSearchStartDate).add(7, 'days').toDate()
  }

  public get searchEndDate() {
    return new Date(this.workFile.desiredEndDate!)
  }

  public get contactEmail(): string | null {
    return this.workFile.contactInfo?.email
  }

  //#endregion

  //#region [Watch]
  //#endregion

  //#region [Methods]
  public async mounted() {
    this.progressIndication.appointing = true
  }


  public onAppointmentInfoChange(value: AppointmentInfo) {
    this.appointment = value
  }

  public async saveAppointment() {
    if (!this.appointment) {
      return
    }

    this.loading = true

    if (this.appointment.noSatisfyingDate) {
      await this.sendCallbackRequest()
      this.progressIndication.appointing = false
      this.progressIndication.sendConfirmation = true
      this.loading = false
      return
    }

    this.$appInsights.trackEvent('WorkFileTracking_AppointmentModification_Updated', {
      Segment: 'WorkFileTracking',
      ZipCode: this.identificationState!.zipCode!.trim(),
      WorksFileId: this.identificationState!.worksFileId!.trim(),
      WorkRequestCode: this.workFile.workRequests[0].code
    })

    const data: UpdateAppointmentRequest = {
      workFileReference: this.workFile.reference!,
      topic: appointmentService.appointmentWorkRequestTopic(this.workFile),
      service: appointmentService.appointmentWorkRequestService(this.workFile),
      workRequestType: appointmentService.appointmentWorkRequestType(this.workFile),
      powerLevel: appointmentService.appointmentPowerLevel(this.workFile),
      workFileStartDate: this.workFile.desiredStartDate!,
      workFileEndDate: this.workFile.desiredEndDate!,
      customerComment: this.appointment.remarks,
      appointmentStartTimestamp: this.appointment.selectedSlot!.startDate!,
      appointmentEndTimestamp: this.appointment.selectedSlot!.endDate!,
      yearlyReadingInformationReadingMonthNumber: null,
      language: 'fr', // lang will be determined in API but cannot be null
      contactInfo: this.workFile.contactInfo ?? {
        name: null,
        mobilePhoneNumber: null,
        housePhoneNumber: null,
        email: null
      },
      reference: this.requestId,
      workRequestCode: this.workFile.workRequests[0].code
    }

    const token = sessionStorage.getItem('token')

    if (!token) {
      throw new Error('No token found in sessionStorage');
    }
    this.createAppointmentStatus = CreateAppointmentStatus.NoError
    const response: AxiosResponse<CreateAppointmentResponse> = await this.$api.post(
      `api/worksrequest/work-file/${this.workFile.reference}/appointment/update`,
      data,
      {
        headers: {
          Authorization: token
        }
      }
    )
    if (response.status >= 200 && response.status < 300) {
      if (response.data.status === CreateAppointmentStatus.AppointmentNotAvailable) {
        this.progressIndication.appointing = true
        this.progressIndication.sendConfirmation = false
        this.createAppointmentStatus = CreateAppointmentStatus.AppointmentNotAvailable
      } else {
        if (response.data.status === CreateAppointmentStatus.ErrorDuringScheduling || response.data.status === CreateAppointmentStatus.ErrorUnSpecified) {
          this.createAppointmentStatus = CreateAppointmentStatus.ErrorDuringScheduling
        }
        this.progressIndication.appointing = false
        this.progressIndication.sendConfirmation = true
      }

    }
    this.loading = false
  }

  public async availabilitiesError() {
    this.progressIndication.appointing = true
    this.progressIndication.sendConfirmation = false
    this.$store.dispatch('apiErrors/customManage')
  }

  public async sendCallbackRequest() {
    this.loading = true
    const token = sessionStorage.getItem('token')
    const data: CallbackRequestCreationRequest = {
      workFileReference: this.workFile.reference!,
      service: appointmentService.appointmentWorkRequestService(this.workFile),
      topic: appointmentService.appointmentWorkRequestTopic(this.workFile),
      powerLevel: appointmentService.appointmentPowerLevel(this.workFile),
      workRequestType: appointmentService.appointmentWorkRequestType(this.workFile),
      subject: 'Prise de rdv web indisponible',
      comment:
        'TL CREEE AUTOMATIQUEMENT : Le client n\'a pas trouvé de date qui lui convient via le formulaire Web. Merci de le recontacter pour fixer un RDV'
    }

    if (!token) {
      throw new Error('No token found in sessionStorage');
    }

    await this.$api.post(`api/worksrequest/work-file/${this.workFile.reference}/callback-request`, data, {
      headers: {
        Authorization: token
      }
    })
    this.$appInsights.trackEvent('WorkFileTracking_CallbackRequest_Created', {
      Segment: 'WorkFileTracking',
      WebRef: this.requestId,
      CallbackRequestReason: 'customer_no_availability_found',
      WorkRequestCode: this.workFile.workRequests[0].code,
      WorkFileReference: this.workFile.reference
    })

    this.loading = false
  }

  public async onSendConfirmationClose() {
    if (!this.appointment || this.appointment.noSatisfyingDate) {
      location.href = this.$t('common.oresLink').toString()
      return
    }
    // Refresh the work file and go back to work file view step
    this.loading = true
    const token = sessionStorage.getItem('token')

    if (!token) {
      throw new Error('No token found in sessionStorage');
    }

    const response = await this.$api.get(`api/WorksRequest/get-work-file/${this.workFile.reference}`, {
      headers: {
        Authorization: token
      }
    })

    this.loading = false

    if (response.status !== 200) {
      this.$store.dispatch('apiErrors/customManage', 'contactCallCenter')
      return false
    }

    const workFileResponse: GetWorkFileResponse = response.data
    this.storeFile(workFileResponse.workFile)

    this.close()
  }

  //#endregion

  //#region [Emit]
  @Emit('close')
  private close(): boolean {
    return true
  }
  //#endregion
}
</script>
